<template>
    <div class="w-80 h-full max-h-full bg-inherit border-r border-solid border-gray-200 dark:border-gray-600">
        <div class="flex flex-none items-center justify-center pb-4 w-full border-b border-solid border-gray-200 dark:border-gray-600 p-4">
            <button 
                data-tour="schedule-add-btn"
                @click="addNewEvent"
                class="flex-grow rounded bg-gradient-to-r from-blue-500 to-purple-500 !cursor-pointer transition-all hover:from-blue-600 hover:to-purple-600 border-none !p-2 !w-full"
            >
                <div class="flex flex-row gap-2 items-center justify-center text-white">
                    <PlusIcon class="size-6 text-white" />
                    <div>
                        {{ $t('schedule.addEvent') }}
                    </div>
                </div>
            </button>
        </div>
        
        <!-- EventMenu internal components -->
        <div class="space-y-4 flex flex-col flex-1">
            <!-- Date Picker -->
            <div class="flex w-full justify-center items-center border-b border-solid border-gray-200 dark:border-gray-600">
                <Datepicker 
                    @update:modelValue="dateSelected"
                    inline="true" 
                    :language="currentLanguageConfig"
                    calendar-class="!shadow-none" 
                    v-model="selectedDate" 
                />
            </div>
            
            <!-- Event Filters -->
            <div class="p-3">
                <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {{ $t('schedule.eventFilters') }}
                </h3>
                <div class="flex flex-col justify-start">
                    <div v-for="label in labels" :key="label.name" class="flex items-center mb-5 mx-2">
                        <div 
                            class="flex items-center rounded-sm p-0.5" 
                            :class="getBgColor(label.color, 600)"
                            :style="getBgStyle(label.color)"
                        >
                            <input 
                                @change="toggleValue(label)" 
                                id="songs" 
                                type="checkbox" 
                                :checked="label.selected"
                                class="w-4 h-4 rounded-sm bg-transparent border-none checked:bg-transparent text-transparent dark:text-transparent focus:ring-0 cursor-pointer"
                            />
                        </div>
                        <label class="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                            {{ $te(`schedule.categories.${label.name}`) ? $t(`schedule.categories.${label.name}`) : label.name }}
                        </label>
                    </div>
                </div>
            </div>

            <!-- Quick Add Events -->
            <div class="p-3 border-t border-solid border-gray-200 dark:border-gray-600">
                <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {{ $t('schedule.quickAdd') }}
                </h3>
                <div class="space-y-2">
                    <button 
                        v-for="quickEvent in quickEvents" 
                        :key="quickEvent.name"
                        @click="addQuickEvent(quickEvent)"
                        class="w-full text-left p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                        <div class="flex items-center space-x-2">
                            <div 
                                class="w-3 h-3 rounded-full"
                                :class="getBgColor(quickEvent.color, 500)"
                                :style="getBgStyle(quickEvent.color)"
                            ></div>
                            <span class="text-sm text-gray-900 dark:text-white">
                                {{ $te(`schedule.categories.${quickEvent.name}`) ? $t(`schedule.categories.${quickEvent.name}`) : quickEvent.name }}
                            </span>
                        </div>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, defineEmits, defineProps, watch, computed } from 'vue'
import { PlusIcon } from '@heroicons/vue/24/outline'
import Datepicker from 'vuejs3-datepicker'
import { useI18n } from 'vue-i18n'
import { getBgColor, getBgStyle } from '@/utilities/color'

const emit = defineEmits(['add-event', 'filter-change'])
const props = defineProps(['categories'])
const { locale, t, te } = useI18n()

const selectedDate = ref(null);
const labels = ref([]);

const quickEvents = ref();

const currentLanguageConfig = computed(() => {
    const loc = locale.value || 'en';
    if (loc.startsWith('pt')) return 'pt';
    if (loc.startsWith('es')) return 'es';
    if (loc.startsWith('fr')) return 'fr';
    return 'en';
})

function dateSelected(payload) {
    selectedDate.value = payload
}

function toggleValue(label) {
    label.selected = !label.selected
    emit('filter-change', labels.value)
}

function addNewEvent() {
    emit('add-event', { date: selectedDate.value || new Date(), time: null })
}

function addQuickEvent(quickEvent) {
    const translatedName = te(`schedule.categories.${quickEvent.name}`) 
        ? t(`schedule.categories.${quickEvent.name}`) 
        : quickEvent.name;

    const eventData = {
        title: translatedName,
        date: selectedDate.value || new Date(),
        categoryId: quickEvent.id,
        description: t('schedule.eventForm.placeholders.quickSession', { category: translatedName.toLowerCase() })
    }
    emit('add-event', eventData)
}
watch(() => props.categories, () => {
    labels.value = props.categories
    quickEvents.value = props.categories;
    emit('filter-change', labels.value)
})
</script>
